import { useContext, useEffect, useRef, useState } from 'react';
import Cross from '../../Assets/Modals/closeIcon.svg';
import ReactCrop, {
    centerCrop,
    makeAspectCrop,
} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

import { canvasPreview } from './canvasPreview';
import { useDebounceEffect } from '../../Hooks/useDebounceEffect';
import { CropImageContext } from '../../Context/CropImageContext';

function CropImage() {
    const { imageToCrop, setCroppedImage, setIsImageCropped, setModalOpen, aspectRatio, setAspectRatio } = useContext(CropImageContext);

    const [imgSrc, setImgSrc] = useState('');

    const containerRef = useRef(null);
    const previewCanvasRef = useRef(null);
    const hiddenAnchorRef = useRef(null);

    const [crop, setCrop] = useState();
    const [completedCrop, setCompletedCrop] = useState();
    // const [scale, setScale] = useState(1);
    const scale = 1;
    // const aspect = 16 / 9

    const imgRef = useRef(null);

    const handleCloseModal = () => {
        setIsImageCropped(false);
        setCroppedImage({});
        setModalOpen(false);
    }

    function centerAspectCrop(
        mediaWidth,
        mediaHeight,
        aspect,
    ) {
        return centerCrop(
            makeAspectCrop(
                {
                    unit: '%',
                    width: 90,
                },
                aspect,
                mediaWidth,
                mediaHeight,
            ),
            mediaWidth,
            mediaHeight,
        )
    }

    const onImageLoad = (e) => {
        if (aspectRatio) {
            const { width, height } = e.currentTarget;
            setCrop(centerAspectCrop(width, height, aspectRatio));
        }
    }

    useDebounceEffect(
        async () => {
            if (
                completedCrop?.width &&
                completedCrop?.height &&
                imgRef.current &&
                previewCanvasRef.current
            ) {

                canvasPreview(
                    imgRef.current,
                    previewCanvasRef.current,
                    completedCrop,
                    scale,
                )
            }
        },
        100,
        [completedCrop, scale],
    );

    const handleSaveCroppedImage = async () => {
        const image = imgRef.current;
        const previewCanvas = previewCanvasRef.current;

        if (!image || !previewCanvas || !completedCrop) {
            throw new Error('Crop canvas does not exist');
        }

        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;

        const offscreen = new OffscreenCanvas(
            completedCrop.width * scaleX,
            completedCrop.height * scaleY,
        )

        const ctx = offscreen.getContext('2d');

        if (!ctx) {
            throw new Error('No 2d context');
        }

        ctx.drawImage(
            previewCanvas,
            0,
            0,
            previewCanvas.width,
            previewCanvas.height,
            0,
            0,
            offscreen.width,
            offscreen.height,
        );

        if(offscreen.width === 0 || offscreen.height === 0) {
            setCroppedImage(imageToCrop);
            setIsImageCropped(true);
        }
        else {
            const blob = await offscreen.convertToBlob({
                type: 'image/jpg',
            });

            const file = new File([blob], 'image.jpg', { type: 'image/jpg' });

            setCroppedImage(file);
            setIsImageCropped(true);
        }
    }

    useEffect(() => {
        if (imageToCrop) {
            const reader = new FileReader();
            reader.addEventListener('load', () =>
                setImgSrc(reader.result?.toString() || ''),
            );
            reader.readAsDataURL(imageToCrop);
        }
    }, [imageToCrop]);

    return (
        <div ref={containerRef} className='w-[38%] h-fit flex flex-col rounded-2xl bg-[#FFFFFF] shadow-[0px_0px_12px_0px_#0000003D]'>
            <div className="flex flex-col">
                <div className="pt-6 pr-2 pb-2 pl-6 flex items-center">
                    <h1 className='w-[calc(100%-44px)] font-semibold text-2xl text-[#121212]'>Edit Image</h1>

                    <button onClick={handleCloseModal} className="outline-none size-11 flex items-center justify-center">
                        <img src={Cross} alt="" />
                    </button>
                </div>

                <div className="bg-black flex items-center justify-center">
                    <ReactCrop
                        className='max-w-80 max-h-96 flex items-center justify-center'
                        crop={crop}
                        onChange={(_, percentCrop) => setCrop(percentCrop)}
                        onComplete={(c) => setCompletedCrop(c)}
                        minHeight={50} 
                        aspect={aspectRatio} 
                    >
                        <img
                            ref={imgRef}
                            alt="Crop me"
                            src={imgSrc}
                            style={{ 
                                transform: `scale(${scale})`, 
                                margin: 'auto',
                                display: 'block',
                            }}
                            onLoad={onImageLoad} 
                        />
                    </ReactCrop>
                </div>
            </div>

            <div>
                <canvas
                    ref={previewCanvasRef}
                    style={{
                        border: '1px solid black',
                        objectFit: 'contain',
                        width: completedCrop?.width,
                        height: completedCrop?.height,
                        display: 'none',
                    }}
                />
            </div>

            <div className="px-6 pt-3 pb-6">
                <button onClick={handleSaveCroppedImage} className='px-4 py-3 rounded bg-primaryColor hover:bg-primaryAccent w-full font-semibold text-base text-buttonText'>Save Image</button>

                <a
                    href="#hidden"
                    ref={hiddenAnchorRef}
                    download
                    style={{
                        position: 'absolute',
                        top: '-200vh',
                        visibility: 'hidden',
                    }}
                    >
                    Hidden download
                </a>
            </div>
        </div>
    );
}

export default CropImage;
